import { createContext, useContext, useState, ReactNode } from 'react';
import {
  modalTitleType,
  modalDataType,
  modalHandlersType,
} from '../types/modal';

export interface ModalContextProps {
  isOpen: boolean;
  modalTitle: modalTitleType;
  modalData: modalDataType;
  modalHandlers: modalHandlersType;
  openModal: (
    title: modalTitleType,
    data: modalDataType,
    handlers: modalHandlersType,
  ) => void;
  closeModal: () => void;
}

export const ModalContext = createContext<ModalContextProps | undefined>({
  isOpen: false,
  modalTitle: '' as modalTitleType,
  modalData: {} as modalDataType,
  modalHandlers: [] as modalHandlersType,
  openModal: (
    title: modalTitleType,
    data: modalDataType,
    handlers: modalHandlersType,
  ) => {},
  closeModal: () => {},
});

export const ModalProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState<modalTitleType>(
    '' as modalTitleType,
  );
  const [modalData, setModalData] = useState<modalDataType>(
    {} as modalDataType,
  );
  const [modalHandlers, setModalHandlers] = useState<modalHandlersType>([]);

  const openModal = (
    title: modalTitleType,
    data: modalDataType,
    handlers: modalHandlersType,
  ) => {
    setModalTitle(title);
    setModalData(data);
    setModalHandlers(handlers);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setModalTitle('' as modalTitleType);
    setModalData({} as modalDataType);
    setModalHandlers([]);
  };

  return (
    <ModalContext.Provider
      value={{
        isOpen,
        openModal,
        closeModal,
        modalTitle,
        modalData,
        modalHandlers,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = (): ModalContextProps => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
